import calcWidth from "./helpers";
import { v4 as uuidv4 } from "uuid";
import { useMjmlStore } from "@/stores/mjmlStore";
import { useApiBlocksStore } from "@/stores/apiBlocks";
import { TopolFeedBlock } from "@/schemas/template/blocks/feedBlockSchema";

export default {
  getData(
    state: any,
    props: { sectionIndex: number; columnIndex: number },
    name: string
  ): TopolFeedBlock {
    const apiBlocksStore = useApiBlocksStore();
    const mjmlStore = useMjmlStore();
    const blockProperties = apiBlocksStore.blocks.get(name);

    if (!blockProperties) {
      throw new Error(`Block ${name} not found`);
    }

    const attributes: any = {
      containerWidth: calcWidth(
        state.mjml.children[0].attributes["containerWidth"],
        state.mjml.children[0].children[props.sectionIndex].children[
          props.columnIndex
        ].attributes["width"]
      ),
      dynamic: true,
      [name + "_id"]: undefined,
      [name + "_feed_id"]: undefined,
    };

    const blockKeys = Object.keys(blockProperties.blockStructure);
    let layout = "";

    blockKeys.forEach((key, index) => {
      let attributesValues: any = {};
      if (key === "layout") {
        layout = blockProperties.blockStructure[key];
      } else if (key === "image") {
        attributesValues = getImageAttributes(
          blockProperties.blockStructure[key],
          name
        );
        // } else if (key === "href") {
        //   attributes[key] =
        //     blockProperties.blockStructure[key].defaultValue || "";
      } else if (key === "button") {
        attributesValues = getButtonAttributes(
          blockProperties.blockStructure[key],
          name
        );
      } else {
        attributesValues = getTextAttributes(
          key,
          blockProperties.blockStructure[key],
          name
        );
      }

      Object.keys(attributesValues).forEach((attrKey) => {
        attributes[attrKey] = attributesValues[attrKey];
      });

      if (!blockKeys.includes("layout") && key !== "href") {
        layout += key + ":show" + (index < blockKeys.length - 1 ? "," : "");
      }
    });

    if (
      mjmlStore.mjml &&
      mjmlStore.mjml.feedBlocksApplyToAll &&
      mjmlStore.mjml.feedBlocksApplyToAll[name] &&
      getBlockInstance(name)
    ) {
      attributes.layout = getBlockInstance(name).attributes.layout;
    } else {
      attributes.layout = layout;
    }

    const hover = getValues(
      name,
      "hover",
      blockProperties.blockStructure["button"],
      undefined,
      "button-"
    );

    return {
      uid: uuidv4(),
      tagName: `mj-feed-block`,
      blockName: name,
      attributes: attributes,
      items: [],
      layout: "column",
      mode: "static",
      maxPerRow: 3,
      syncButtonText: true,
      children: [
        {
          tagName: "mj-column",
          uid: uuidv4(),
          attributes: {
            "vertical-align": "top",
            padding: "0",
            width: "100%",
          },
          children: [],
        },
      ],
      ...(hover ? { "button-hover": hover } : {}),
    };
  },
};

function getImageAttributes(imageProperties: any, blockName: string) {
  return {
    "columns-layout": getValues(
      blockName,
      "columns-layout",
      imageProperties,
      "one-column"
    ),
    "image-width": getValues(
      blockName,
      "width",
      imageProperties,
      300,
      "image-"
    ),
    "image-alt": imageProperties.alt || "",
    "image-title": imageProperties.title || "",
    "image-fluid-on-mobile": getValues(
      blockName,
      "fluidOnMobile",
      imageProperties,
      "false",
      "image-"
    ),
    "image-align": getValues(
      blockName,
      "align",
      imageProperties,
      "center",
      "image-"
    ),
  };
}

function getButtonAttributes(buttonProperties: any, blockName: string) {
  return {
    "button-align": getValues(
      blockName,
      "align",
      buttonProperties,
      "center",
      "button-"
    ),
    "button-text-align": getValues(
      blockName,
      "text-align",
      buttonProperties,
      "center",
      "button-"
    ),
    "button-width": getValues(
      blockName,
      "width",
      buttonProperties,
      "auto",
      "button-"
    ),
    "button-background-color": getValues(
      blockName,
      "background-color",
      buttonProperties,
      "#e85034",
      "button-"
    ),
    "button-gradient": getValues(
      blockName,
      "gradient",
      buttonProperties,
      "",
      "button-"
    ),
    "button-color": getValues(
      blockName,
      "color",
      buttonProperties,
      "#ffffff",
      "button-"
    ),
    "button-font-size": getValues(
      blockName,
      "font-size",
      buttonProperties,
      "13px",
      "button-"
    ),
    "button-font-family": getValues(
      blockName,
      "font-family",
      buttonProperties,
      "Helvetica, Arial, sans-serif",
      "button-"
    ),
    "button-font-weight": getValues(
      blockName,
      "font-weight",
      buttonProperties,
      "normal",
      "button-"
    ),
    "button-font-style": getValues(
      blockName,
      "font-style",
      buttonProperties,
      "normal",
      "button-"
    ),
    "button-text-transform": getValues(
      blockName,
      "text-transform",
      buttonProperties,
      "none",
      "button-"
    ),
    "button-text-decoration": getValues(
      blockName,
      "text-decoration",
      buttonProperties,
      "none",
      "button-"
    ),
    "button-border-radius": getValues(
      blockName,
      "border-radius",
      buttonProperties,
      "3px",
      "button-"
    ),
    "button-padding": getValues(
      blockName,
      "padding",
      buttonProperties,
      "5px 5px 5px 5px",
      "button-"
    ),
    "button-inner-padding": getValues(
      blockName,
      "inner-padding",
      buttonProperties,
      "10px 20px 10px 20px",
      "button-"
    ),
  };
}

function getTextAttributes(
  name: string,
  textProperties: any,
  blockName: string
) {
  return {
    [name + "-font-size"]: getValues(
      blockName,
      "font-size",
      textProperties,
      "13px",
      name + "-"
    ),
    [name + "-color"]: getValues(
      blockName,
      "color",
      textProperties,
      "#000000",
      name + "-"
    ),
    [name + "-font-family"]: getValues(
      blockName,
      "font-family",
      textProperties,
      "Helvetica, Arial, sans-serif",
      name + "-"
    ),
    [name + "-font-weight"]: getValues(
      blockName,
      "font-weight",
      textProperties,
      "normal",
      name + "-"
    ),
    [name + "-font-style"]: getValues(
      blockName,
      "font-style",
      textProperties,
      "normal",
      name + "-"
    ),
    [name + "-text-transform"]: getValues(
      blockName,
      "text-transform",
      textProperties,
      "none",
      name + "-"
    ),
    [name + "-text-decoration"]: getValues(
      blockName,
      "text-decoration",
      textProperties,
      "none",
      name + "-"
    ),
    [name + "-padding"]: getValues(
      blockName,
      "padding",
      textProperties,
      "5px 5px 5px 5px",
      name + "-"
    ),
    [name + "-align"]: getValues(
      blockName,
      "align",
      textProperties,
      "center",
      name + "-"
    ),
  };
}

function getValues(
  blockName: string,
  property: string,
  values: any,
  defaultValue?: string | number | boolean,
  prefix = ""
) {
  const mjmlStore = useMjmlStore();
  if (
    mjmlStore.mjml &&
    mjmlStore.mjml.feedBlocksApplyToAll &&
    mjmlStore.mjml.feedBlocksApplyToAll[blockName]
  ) {
    const blockInstance = getBlockInstance(blockName);
    if (blockInstance) {
      return blockInstance.attributes[
        prefix + (property === "fluidOnMobile" ? "fluid-on-mobile" : property)
      ];
    }
  }
  const value = values[property];
  if (value === undefined) {
    return defaultValue;
  }
  if (Array.isArray(value)) {
    return mjmlStore.getBaseAttrString(value[0], property) || value[1];
  }
  return value;
}

function getBlockInstance(blockName: string) {
  const mjmlStore = useMjmlStore();
  let blockInst: any = undefined;

  if (
    mjmlStore.mjml &&
    mjmlStore.mjml.children &&
    mjmlStore.mjml.children[0] &&
    mjmlStore.mjml.children[0].children
  ) {
    for (const section of mjmlStore.mjml.children[0].children) {
      if (blockInst) break;
      if (!section || !section.children) continue;
      for (const column of section.children) {
        if (!column || !column.children) continue;
        for (const block of column.children) {
          if (
            !block ||
            (block.tagName !== "mj-feed-block" &&
              block.tagName !== "mj-article" &&
              block.tagName !== "mj-product")
          )
            continue;
          if (block.blockName === blockName) {
            blockInst = block;
            break;
          }
        }
      }
    }
  }
  return blockInst;
}
